import { useState, useRef, KeyboardEvent } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  InputAdornment,
  Tooltip
} from '@mui/material';
import { 
  Send as SendIcon, 
  LocationOn as LocationIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon 
} from '@mui/icons-material';
import { useTranslation } from '@fanfanlo';
import { matrixClient } from '../../client';
import { locationMessageUtils } from '../../client/message/handlers/location';
import { LocationPicker } from '../messages/location/LocationPicker';

interface MessageInputProps {
  roomId: string;
}

export function MessageInput({ roomId }: MessageInputProps) {
  const { t } = useTranslation('homepage/im/components/room/content');
  const [message, setMessage] = useState('');
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const sendMessage = async () => {
    if (!message.trim()) return;
    
    try {
      await matrixClient.client.sendTextMessage(roomId, message.trim());
      setMessage('');
      // 保持焦点在输入框
      textareaRef.current?.focus();
    } catch (error) {
      console.error('发送消息失败:', error);
    }
  };

  const handleLocationConfirm = async (location: { 
    latitude: number; 
    longitude: number; 
    title: string;
    description: string;
  }) => {
    try {
      await locationMessageUtils.send(
        roomId,
        location.latitude,
        location.longitude,
        location.title,
        location.description
      );
    } catch (error) {
      console.error('发送位置消息失败:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 处理文件上传
      console.log('Selected file:', file);
      // TODO: 实现文件上传逻辑
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <Tooltip title={t('MessageInput.attachFile')}>
          <IconButton onClick={() => fileInputRef.current?.click()}>
            <AttachFileIcon />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('MessageInput.emoji')}>
          <IconButton>
            <EmojiIcon />
          </IconButton>
        </Tooltip>
        
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          placeholder={t('MessageInput.placeholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          inputRef={textareaRef}
          sx={{ mx: 1 }}
          InputProps={{
            sx: { borderRadius: 4, pr: 1 },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip 
                  title={t('MessageInput.sendLocation')} 
                  disableFocusListener 
                  disableHoverListener
                >
                  <IconButton
                    onClick={() => setLocationPickerOpen(true)}
                    edge="end"
                    sx={{ mr: 0.5 }}
                  >
                    <LocationIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        
        <Tooltip 
          title={t('MessageInput.send')}
        >
          <span>
            <IconButton 
              color="primary" 
              onClick={sendMessage}
              disabled={!message.trim()}
              sx={{ alignSelf: 'flex-end', mb: 0.5 }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <LocationPicker
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        onConfirm={handleLocationConfirm}
      />
    </Box>
  );
}
